import type { keyMapItem, mouseKey } from '@rapoo/device-sdk'
import { parseNumber } from './keyIdentity'
import i18n from '@/locales'
import type {
  KeyChangePayload,
  KeyDisplayOverride,
  KeySetting,
  KeyType,
  LayoutKey,
  ResolvedFactoryKey,
} from '../types'
import { KeyConfig } from '@rapoo/element'
import { ResolveMappedMetaResult, LocalizedSpecialKeyMapItem } from '../types'
import { SPECIAL_KEY_META_ITEMS } from './specialKeyMeta'
import {
  KEYCODE_KEYWORDS_MAP,
  KEYCODE_META_MAP,
  getKeyConfigByTypeCode,
  getLocalizedField,
  makeLookupKey,
} from './keycodeMap'
import {
  isMappingMatchFactory,
  matchDisplayOverride,
  readMappingCode,
  resolveFactoryKey,
  resolveOverrideLabel,
} from './factoryKeys'

export { KEYCODE_KEYWORDS_MAP, getKeyConfigByTypeCode, getLocalizedField }

const tk = (key: string, named?: Record<string, unknown>) =>
  (named ? i18n.global.t(key, named) : i18n.global.t(key)) as string

/**
 * 四个全局配置键以及两个切换配置键
 */
export const CONFIG_KEY_ITEMS: (LocalizedSpecialKeyMapItem & { type: number; code: string })[] = [
  {
    type: 9,
    code: '0x28',
    keywords: 'prev config', // 切换上一个配置
    keyType: 'configuration',
    icon: 'Hardware-Wireframe-PrevConfig',
  },
  {
    type: 9,
    code: '0x27',
    keywords: 'next config', // 切换下一个配置
    keyType: 'configuration',
    icon: 'Hardware-Wireframe-NextConfig',
  },
  {
    type: 9,
    code: '0x24',
    keywords: 'config0',
    keyType: 'configuration',
    icon: 'Hardware-Wireframe-proflie1',
    confIndex: 0,
  },
  {
    type: 9,
    code: '0x24',
    keywords: 'config1',
    keyType: 'configuration',
    icon: 'Hardware-Wireframe-proflie2',
    confIndex: 1,
  },
  {
    type: 9,
    code: '0x24',
    keywords: 'config2',
    keyType: 'configuration',
    icon: 'Hardware-Wireframe-proflie3',
    confIndex: 2,
  },
  {
    type: 9,
    code: '0x24',
    keywords: 'config3',
    keyType: 'configuration',
    icon: 'Hardware-Wireframe-proflie4',
    confIndex: 3,
  },
]

/**
 * V700diy-87 三个灯效功能键（背光/侧灯/装饰灯切换）。
 *
 * 这三个键的“默认（未改键）态”语义上为禁用/空：
 * - 设备回读到灯效功能(type 9) 时视为“已改键”，改键页据此展示对应 icon；
 * - 恢复后下发禁用键(type 0)，回读时视为“未改键”，按键展示为空。
 *
 * 仅对该型号生效，避免影响其它键盘把这些 keywords 用作 Fn 功能键的通用逻辑。
 */
export const V700DIY87_MODEL = 'V700diy-87'

/**
 * 三个灯效功能键的图标映射（背光/侧灯/装饰灯切换）。
 * keyword 仅作内部唯一标识，界面上不展示该名称；灯光页与改键页共用此图标。
 */
export const V700DIY87_SPECIAL_ICON_MAP: Record<string, string> = {
  LEDMode_Forward: 'LightEffect-Wireframe-BacklightSwitch',
  SideLED_Mode: 'LightEffect-Wireframe-SideLightSwitch',
  LED_circulate: 'LightEffect-Wireframe-DecorLight',
}

export const isV700Diy87SpecialKey = (model?: string, keywords?: string): boolean =>
  model === V700DIY87_MODEL && !!keywords && keywords in V700DIY87_SPECIAL_ICON_MAP

/**
 * 特殊键元数据索引：`type_code -> SpecialKeyMapItem[]`。
 * 使用数组是为了兼容同一 type/code 下按 confIndex 区分的配置键。
 */
const SPECIAL_META_MAP = new Map<
  string,
  (LocalizedSpecialKeyMapItem & { type?: number; code?: string })[]
>()
for (const item of SPECIAL_KEY_META_ITEMS) {
  const config = KEYCODE_KEYWORDS_MAP.get(item.keywords)
  if (!config) continue
  const type = parseNumber(config.type)
  const code = parseNumber(config.code)
  if (type == null || code == null) continue

  const key = makeLookupKey(type, code)
  const list = SPECIAL_META_MAP.get(key) ?? []
  list.push(item)
  SPECIAL_META_MAP.set(key, list)
}
for (const item of CONFIG_KEY_ITEMS) {
  const type = parseNumber(item.type)
  const code = parseNumber(item.code)
  if (type == null || code == null) continue

  const key = makeLookupKey(type, code)
  const list = SPECIAL_META_MAP.get(key) ?? []
  list.push(item)
  SPECIAL_META_MAP.set(key, list)
}

/**
 * Fn / RT 物理键：不可作为改键目标，且不展示为已改键。
 */
export const REMAP_RESTRICTED_KEYWORDS = new Set(['Fn', 'RT'])

export const isRemapRestrictedKey = (keywords?: string | null) => {
  return Boolean(keywords && REMAP_RESTRICTED_KEYWORDS.has(keywords))
}

/**
 * 判断已解析出的 setting 是否仍停留在出厂功能。
 * 主判定在 `isMappingMatchFactory`（比对固件回读值），这里按 keywords 再复核一次。
 */
export const isSettingAtFactoryDefault = (
  setting: KeySetting | undefined,
  resolved?: ResolvedFactoryKey,
): boolean => {
  if (!resolved || !setting) return false
  return setting.target?.keywords === resolved.keywords
}

/**
 * 获取特殊键元数据：
 * - 传 confIndex 时，优先精确匹配配置索引；
 * - 未传时，优先无 confIndex 的通用项。
 */
const getSpecialMeta = ({
  type,
  code,
  confIndex,
}: {
  type?: number
  code?: number
  confIndex?: number
}) => {
  if (type == null || code == null) return undefined
  const list = SPECIAL_META_MAP.get(makeLookupKey(type, code))
  if (!list?.length) return undefined

  if (confIndex != null) {
    return (
      list.find((item) => item.confIndex === confIndex) ??
      list.find((item) => item.confIndex == null)
    )
  }

  return list.find((item) => item.confIndex == null) ?? list[0]
}

/**
 * 解析目标映射元信息（label/keyType/icon）。
 * 优先使用 keycode 元数据，若不存在则回退到 SPECIAL_KEY_META_ITEMS。
 */
const resolveMappedMeta = ({
  type,
  code,
  confIndex,
}: {
  type?: number
  code?: number
  confIndex?: number
}): ResolveMappedMetaResult | undefined => {
  if (type == null || code == null) return undefined

  const lookupKey = makeLookupKey(type, code)
  const keyMeta = KEYCODE_META_MAP.get(lookupKey)
  const specialMeta = getSpecialMeta({ type, code, confIndex })

  const keywords = keyMeta?.keywords || specialMeta?.keywords || ''

  // const labelFromKeycode = resolveKeyName(keyMeta?.name) || keyMeta?.keywords || ''
  // const labelFromSpecial = specialMeta?.labelKey
  //   ? tk(specialMeta.labelKey)
  //   : specialMeta?.label || specialMeta?.keywords || ''
  // const label = labelFromKeycode || labelFromSpecial || ''
  // const rawLabel = keyMeta?.keywords || resolveKeyName(keyMeta?.name) || labelFromSpecial || label
  // const alias = resolveKeyName(keyMeta?.alias) || labelFromSpecial || label

  const payload: ResolveMappedMetaResult = {
    keywords,
    keyType: (specialMeta?.keyType as ResolveMappedMetaResult['keyType']) ?? 'normal',
  }

  if (confIndex) payload.confIndex = confIndex
  if (specialMeta?.icon) payload.icon = specialMeta.icon

  return payload

  // return {
  //   keywords,
  //   keyType: (specialMeta?.keyType as ResolveMappedMetaResult['keyType']) ?? 'normal',
  //   // mappedKeyCode: specialMeta?.keywords,
  //   icon: specialMeta?.icon,
  //   confIndex,
  // }
}

/**
 * 组装所有模式都共享的 raw 字段。
 */
const createBaseRaw = (item: keyMapItem, originalKey: LocalizedSpecialKeyMapItem | KeyConfig) => {
  // const keyMeta = originalKey.keywords ? KEYCODE_KEYWORDS_MAP.get(originalKey.keywords) : undefined
  return {
    index: item.index,
    originalLabel:
      originalKey && 'alias' in originalKey ? getLocalizedField(originalKey?.alias) : '',
    // sourceType: parseNumber(keyMeta?.type),
    // sourceCode: parseNumber(keyMeta?.code),
    // sourceConfIndex: parseNumber(originalKey.confIndex as string | undefined),
    type: item.keyAttr.type,
  }
}

/**
 * 常规 code+type 映射解析（normal / media / configuration）。
 */
const MOUSE_KEY_TO_CODE: Record<number, number> = { 0: 0xf0, 1: 0xf1, 2: 0xf2, 3: 0xf3, 4: 0xf4 }

const CODE_TO_MOUSE_BIT: Record<number, number> = Object.fromEntries(
  Object.entries(MOUSE_KEY_TO_CODE).map(([bit, code]) => [code, Number(bit)]),
)

const buildCodeMappingSetting = (
  item: keyMapItem,
  originalKey: LayoutKey,
): KeySetting | undefined => {
  const targetType = parseNumber(item.keyAttr.type)
  if (targetType == null || !originalKey.keywords) return undefined

  let targetCode = item.keyAttr.code?.[0]

  // type 0x04: 从 mouseKey 位映射推导虚拟 code 用于查 SPECIAL_META_MAP
  if (targetType === 0x04) {
    const mk = item.keyAttr.mouse?.mouseKey
    const bitIndex = mk?.findIndex((v: number) => v === 1) ?? -1
    targetCode = bitIndex >= 0 ? MOUSE_KEY_TO_CODE[bitIndex] : undefined
  }

  // 鼠标其他功能（0x05/0x06/0x07）TODO:

  if (targetCode == null) return undefined

  const mapped = resolveMappedMeta({
    type: targetType,
    code: targetCode,
    confIndex: item.keyAttr.confIndex,
  })

  // type/code 在 keycode / 特殊键表中均无法识别（如固件空白占位 type9+0x31）→ 不展示为已改键
  if (!mapped?.keywords) return undefined

  const mappedConfig =
    KEYCODE_KEYWORDS_MAP.get(mapped.keywords) ??
    CONFIG_KEY_ITEMS.find((item) => item.keywords === mapped.keywords)

  if (!mappedConfig) return undefined

  return {
    keywords: originalKey.keywords,
    isModified: true,
    keyType: mapped.keyType ?? 'normal',
    target: mappedConfig as KeyConfig,
    raw: {
      ...createBaseRaw(item, mappedConfig as KeyConfig),
      confIndex: item.keyAttr.confIndex,
      icon: mapped.icon ?? '',
    },
  }
}

/**
 * 把 MT/TGL 的子键 type+code 解析成可读文案。
 */
const resolveDualModeKeyLabel = (type?: number, code?: number) => {
  if (type == null || code == null) return ''
  const config = getKeyConfigByTypeCode(type, code)
  if (config?.alias) return getLocalizedField(config.alias)
  else return ''
}

/**
 * MT/TGL 解析。
 */
const buildDualModeSetting = (
  item: keyMapItem,
  originalKey: LayoutKey,
  mode: Extract<KeyType, 'MT' | 'TGL'>,
): KeySetting | undefined => {
  if (!originalKey.keywords || !item.keyAttr.dualModeKey) return undefined

  // 单键的按键类型都是 type=0x01，和clickKeyType/longPressType对应的功能不同
  const dualModeKey = item.keyAttr.dualModeKey
  const clickKeyLabel = resolveDualModeKeyLabel(0x01, dualModeKey.clickKeyCode)
  const pressKeyLabel = resolveDualModeKeyLabel(0x01, dualModeKey.longPressKeyCode)
  // const label = `${clickKeyLabel}/${pressKeyLabel}`

  const mappedConfig = KEYCODE_KEYWORDS_MAP.get(originalKey.keywords)

  return {
    keywords: originalKey.keywords,
    isModified: true,
    keyType: mode,
    target: null,
    raw: {
      ...createBaseRaw(item, mappedConfig!),
      dualModeKey,
      clickKeyLabel,
      pressKeyLabel,
    },
  }
}

/**
 * macro/DKS/RS/SOCD 的统一结构化解析。
 */
const buildStructuredSetting = (
  item: keyMapItem,
  originalKey: LayoutKey,
  mode: Extract<KeyType, 'macro' | 'DKS' | 'RS' | 'SOCD'>,
  extraRaw: Record<string, unknown>,
): KeySetting | undefined => {
  if (!originalKey.keywords) return undefined

  const mappedConfig = KEYCODE_KEYWORDS_MAP.get(originalKey.keywords)

  return {
    keywords: originalKey.keywords,
    isModified: true,
    keyType: mode,
    target: null,
    raw: {
      ...createBaseRaw(item, mappedConfig!),
      ...extraRaw,
    },
  }
}

/**
 * 判断是否与布局默认映射一致，若一致则无需生成 keySetting。
 */
const isUnchangedDefaultMapping = (item: keyMapItem, originalKey: LayoutKey): boolean => {
  const keyMeta = originalKey.keywords ? KEYCODE_KEYWORDS_MAP.get(originalKey.keywords) : undefined
  const sourceCode = parseNumber(keyMeta?.code)
  const sourceType = parseNumber(keyMeta?.type)
  const targetCode = item.keyAttr.code?.[0]
  const targetType = parseNumber(item.keyAttr.type)

  if (sourceCode == null || sourceType == null || targetCode == null || targetType == null)
    return false
  if (sourceCode !== targetCode || sourceType !== targetType) return false

  // 仅配置键（keycode 带 confIndex）才比对 confIndex
  if (targetType === 0x09) {
    const sourceConfIndex = parseNumber(keyMeta?.confIndex as string | undefined)
    if (sourceConfIndex != null) {
      return sourceConfIndex === item.keyAttr.confIndex
    }
  }

  return true
}

/**
 * 把设备返回的 keyMap 转为 UI 需要的 keySettings。
 *
 * 解析策略：
 * 1. 根据 index 找到目标键；
 * 2. Fn/RT 物理键一律过滤（不展示已改键）；
 * 3. 命中 displayOverrides.hidden 的映射不展示；
 * 4. 与出厂定义一致 → 生成 setting（已改样式 + isFactoryDefault）；
 * 5. 无出厂定义且与布局物理键一致 → 不生成 setting；
 * 6. 其余按 keyAttr.type 分发处理。
 */
export const parseLayerMapToKeySettings = ({
  keyMap,
  layoutKeys,
  model,
  factoryKeys,
  displayOverrides,
  isFnLayer = false,
}: {
  keyMap: keyMapItem[]
  layoutKeys: LayoutKey[][]
  model?: string
  /** 当前 Win/Mac 层的 Fn 出厂映射：物理 keywords → 出厂功能 keywords */
  factoryKeys?: Record<string, string>
  /** 固件回读中需要隐藏或改描述的键 */
  displayOverrides?: KeyDisplayOverride[]
  isFnLayer?: boolean
}): KeySetting[] => {
  // 同一个目标键只保留最后一次有效映射，防止重复项污染 UI。
  const settingsByTargetKey = new Map<string, KeySetting>()

  // 布局键的 key 字段即为横向 index（非全尺寸键盘存在间隔），按 index 建立映射用于 O(1) 定位。
  const layoutByIndex = new Map<number, LayoutKey>()
  layoutKeys.flat().forEach((key) => {
    if (key?.key != null) layoutByIndex.set(key.key, key)
  })

  keyMap.forEach((item) => {
    const originalKey = layoutByIndex.get(item.index)

    /**
     * originalKey?.keywords !== '' 后续可能会出现bug
     * 这个判断是针对v700diy-87三个自定义键通过判断
     */
    if (!originalKey?.keywords) return

    // Fn / RT：始终不进入已改键列表
    if (isRemapRestrictedKey(originalKey.keywords)) return

    const targetType = parseNumber(item.keyAttr.type)
    if (targetType == null) return

    const targetCode = readMappingCode(item)

    // 命中隐藏规则的映射一律不展示为已改键
    const override = matchDisplayOverride(displayOverrides, {
      keywords: resolveMappedMeta({
        type: targetType,
        code: targetCode,
        confIndex: item.keyAttr.confIndex,
      })?.keywords,
      type: targetType,
      code: targetCode,
    })
    if (override?.hidden) return

    const factoryResolved = isFnLayer
      ? resolveFactoryKey(factoryKeys?.[originalKey.keywords])
      : undefined
    const matchesFactory = isMappingMatchFactory(item, factoryResolved)
    const matchesPhysical = isUnchangedDefaultMapping(item, originalKey)

    // V700diy-87 三个灯效功能键：出厂默认即这三个键的灯效功能，视为“未改键”。
    // 回读为该键默认 type/code（忽略 confIndex）=> 未改键（不生成 setting，改键页展示对应 icon）；
    // 被改成其它键 => 已改键（继续解析，展示新键）。
    if (isV700Diy87SpecialKey(model, originalKey.keywords)) {
      const keyMeta = KEYCODE_KEYWORDS_MAP.get(originalKey.keywords)
      const sourceType = parseNumber(keyMeta?.type)
      const sourceCode = parseNumber(keyMeta?.code)
      if (targetType === sourceType && targetCode === sourceCode) return
    } else if (matchesFactory) {
      // 出厂态：按已改键样式展示，tooltip 只给只读提示
      const setting = buildCodeMappingSetting(item, originalKey)
      if (setting) {
        setting.isFactoryDefault = true
        setting.isModified = true
        const overrideLabel = resolveOverrideLabel(override)
        if (overrideLabel) setting.displayLabel = overrideLabel
        settingsByTargetKey.set(originalKey.keywords, setting)
      }
      return
    } else if (matchesPhysical && !factoryResolved) {
      // 无出厂定义时，与物理键一致视为未改键；有出厂定义却改回物理键算已改键
      return
    }

    let setting: KeySetting | undefined

    switch (targetType) {
      // 宏
      case 0x08: {
        if (!item.keyAttr.macro) return
        const macroIndex = item.keyAttr.macro.index
        setting = buildStructuredSetting(item, originalKey, 'macro', {
          macro: item.keyAttr.macro,
          macroName: `${tk('keys.macro.label')} ${macroIndex ?? ''}`.trim(),
        })
        break
      }
      // MT
      case 0x0a:
        setting = buildDualModeSetting(item, originalKey, 'MT')
        break
      // TGL
      case 0x0b:
        setting = buildDualModeSetting(item, originalKey, 'TGL')
        break
      // DKS
      case 0x0c: {
        if (!item.keyAttr.dks) return
        const dksLabels = (item.keyAttr.dks.keyCodes ?? [])
          .filter((c: number) => c !== 0)
          .map((c: number) => {
            const meta = KEYCODE_META_MAP.get(makeLookupKey(1, c))
            return meta?.name ?? `0x${c.toString(16)}`
          })
        const dksKeyLabels = dksLabels.join('/')
        setting = buildStructuredSetting(item, originalKey, 'DKS', {
          dks: item.keyAttr.dks,
          dksKeyLabels,
        })
        break
      }
      // TODO: 0x05 鼠标键功能、0x06 垂直滚轮、0x07 水平滚轮 暂无 SPECIAL_KEY_META 条目，待补充
      case 0x05:
      case 0x06:
      case 0x07:
        break
      // RS
      case 0x0d: {
        if (!item.keyAttr.rs) return
        setting = buildStructuredSetting(item, originalKey, 'RS', { rs: item.keyAttr.rs })
        break
      }
      // SOCD
      case 0x0e: {
        if (!item.keyAttr.socd) return
        setting = buildStructuredSetting(item, originalKey, 'SOCD', {
          socd: item.keyAttr.socd,
        })
        break
      }
      // 常规 / 多媒体 / 配置 / Fn 特殊键（统一 code+type 解析）
      default:
        setting = buildCodeMappingSetting(item, originalKey)
        break
    }

    if (!setting) return

    // 有出厂定义：再复核一次是否仍为出厂（兜底上面的 type/code 匹配失败）
    if (factoryResolved) {
      setting.isFactoryDefault = isSettingAtFactoryDefault(setting, factoryResolved)
    }

    const overrideLabel = resolveOverrideLabel(override)
    if (overrideLabel) setting.displayLabel = overrideLabel

    settingsByTargetKey.set(originalKey.keywords!, setting)
  })

  return Array.from(settingsByTargetKey.values())
}

const resolveCodeValue = (value: unknown): number | undefined => {
  if (Array.isArray(value)) return parseNumber(value[0])
  return parseNumber(value)
}

/**
 * 从 keySetting 中解析目标键位索引。
 *
 * @param setting 键位设置
 * @returns index；缺失时返回 undefined
 */
const resolveIndex = (setting?: KeySetting): number | undefined => {
  return parseNumber(setting?.raw?.index)
}

/**
 * 组装 UI 改键后写入 setting.raw 的统一结构。
 *
 * @param params 构建参数
 * @returns 标准化 raw 对象
 */
const createSettingRawFromPayload = ({
  sourceData,
  index,
}: {
  sourceData: KeyChangePayload
  index: number
}) => {
  const confIndex = parseNumber(sourceData.raw?.confIndex)

  return {
    index,
    type: sourceData.raw?.type,
    // code: mappedCode,
    confIndex,
    // mappedKeyCode,
    icon: sourceData.raw.icon || '',
    dualModeKey: sourceData.raw?.dualModeKey,
    clickKeyLabel: sourceData.raw?.clickKeyLabel,
    pressKeyLabel: sourceData.raw?.pressKeyLabel,
    macro: sourceData.raw?.macro,
    macroName: sourceData.raw?.macroName,
    dks: sourceData.raw?.dks,
    dksIndex: sourceData.raw?.dksIndex,
    dksKeyLabels: sourceData.raw?.dksKeyLabels,
    mt: sourceData.raw?.mt,
    mtIndex: sourceData.raw?.mtIndex,
    mtDelaySteps: sourceData.raw?.mtDelaySteps,
    tgl: sourceData.raw?.tgl,
    tglIndex: sourceData.raw?.tglIndex,
    relatedKeyIndex: sourceData.raw?.relatedKeyIndex,
    relatedKeyKeywords: sourceData.raw?.relatedKeyKeywords,
    relatedKeyLabel: sourceData.raw?.relatedKeyLabel,
    peerMt: sourceData.raw?.peerMt,
    peerKeyIndex: sourceData.raw?.peerKeyIndex,
    peerMtIndex: sourceData.raw?.peerMtIndex,
    rs: sourceData.raw?.rs,
    socd: sourceData.raw?.socd,
    originalLabel:
      sourceData.raw?.originalLabel ||
      getLocalizedField((sourceData.target as KeyConfig)?.alias || ''),
  }
}

/**
 * 把 UI 改键 payload 统一为 keySetting 结构，保证 raw 中始终存在 index。
 *
 * @param params 构建参数
 * @returns 统一后的 keySetting；目标键无 keywords 时返回 undefined
 */
export const buildKeySettingFromPayload = ({
  sourceData,
  index,
}: {
  sourceData: KeyChangePayload
  index: number
}): KeySetting | undefined => {
  if (!sourceData?.keywords) return undefined

  return {
    keywords: sourceData.keywords,
    isModified: true,
    keyType: sourceData.keyType,
    target: sourceData.target,
    raw: createSettingRawFromPayload({
      sourceData,
      index,
    }),
  }
}

/**
 * 把统一后的 keySetting 转为 SDK 发送结构。
 *
 * @param setting 键位设置
 * @returns SDK 所需 payload；无法构建时返回 undefined
 */
export const buildSdkKeyMapPayloadFromSetting = (setting: KeySetting): keyMapItem | undefined => {
  const index = resolveIndex(setting)
  if (index == null) return undefined

  const type = parseNumber((setting.target as KeyConfig)?.type ?? setting.raw.type)
  if (type == null) return undefined

  const payload: keyMapItem = {
    index,
    keyAttr: {
      type,
    },
  }

  if (setting.keyType === 'MT' || setting.keyType === 'TGL') {
    if (!setting.raw?.dualModeKey) return undefined
    payload.keyAttr.dualModeKey = setting.raw.dualModeKey
    return payload
  }

  if (setting.keyType === 'macro') {
    if (!setting.raw?.macro) return undefined
    payload.keyAttr.macro = setting.raw.macro
    return payload
  }

  if (setting.keyType === 'DKS') {
    if (!setting.raw?.dks) return undefined
    payload.keyAttr.dks = setting.raw.dks
    return payload
  }

  if (setting.keyType === 'RS') {
    if (!setting.raw?.rs) return undefined
    payload.keyAttr.rs = setting.raw.rs
    return payload
  }

  if (setting.keyType === 'SOCD') {
    if (!setting.raw?.socd) return undefined
    payload.keyAttr.socd = setting.raw.socd
    return payload
  }

  const code = resolveCodeValue((setting.target as KeyConfig)?.code)
  if (code == null) return undefined

  // [0x05, 0x06, 0x07] 其他鼠标键暂时不做
  if ([0x04].includes(type)) {
    const mouseKey: mouseKey = [0, 0, 0, 0, 0]
    mouseKey[CODE_TO_MOUSE_BIT[code]] = 1

    payload.keyAttr.mouse = {
      colWheelNum: 0,
      rowWheelNum: 0,
      x: 0,
      y: 0,
      mouseKey,
    }
  }

  payload.keyAttr.code = [code]

  if (setting.keyType === 'configuration') {
    const confIndex = parseNumber(setting.raw?.confIndex ?? setting.raw?.index)
    if (confIndex != null) {
      payload.keyAttr.confIndex = confIndex
    }
  }

  return payload
}

/**
 * 根据已保存在 setting.raw 的 index 生成恢复键位的 payload。
 * 若传入 factoryKeywords，则恢复到出厂功能；否则恢复到物理键。
 *
 * @param params 构建参数
 * @returns SDK 所需恢复 payload；无法构建时返回 undefined
 */
export const buildRestoreKeyMapPayload = ({
  setting,
  defaultKey,
  factoryKeywords,
}: {
  setting?: KeySetting
  defaultKey: {
    keywords?: string
    type?: unknown
    code?: unknown
    confIndex?: unknown
    key?: unknown
  }
  factoryKeywords?: string
}): keyMapItem | undefined => {
  const index = resolveIndex(setting) ?? parseNumber(defaultKey.key)
  if (index == null) return undefined

  const restoreKeywords = factoryKeywords || defaultKey.keywords
  const keyMeta = restoreKeywords ? KEYCODE_KEYWORDS_MAP.get(restoreKeywords) : undefined
  const type = parseNumber(keyMeta?.type ?? defaultKey.type)
  const code = parseNumber(
    keyMeta?.code ?? (Array.isArray(defaultKey.code) ? defaultKey.code[0] : defaultKey.code),
  )

  if (type == null || code == null) return undefined

  const payload: keyMapItem = {
    index,
    keyAttr: {
      type,
      code: [code],
    },
  }

  if (type === 0x09) {
    const confIndex = parseNumber(
      (keyMeta as { confIndex?: unknown } | undefined)?.confIndex ?? defaultKey.confIndex,
    )
    if (confIndex != null) {
      payload.keyAttr.confIndex = confIndex
    }
  }

  return payload
}

/**
 * 根据出厂功能 keywords 组装「恢复为出厂默认」后的 UI setting。
 */
export const buildFactoryDefaultSetting = ({
  physicalKeywords,
  factoryKeywords,
  index,
}: {
  physicalKeywords: string
  factoryKeywords?: string
  index: number
}): KeySetting | undefined => {
  const resolved = resolveFactoryKey(factoryKeywords)
  if (!resolved) return undefined

  const { type, code, confIndex } = resolved
  const factoryMeta = KEYCODE_KEYWORDS_MAP.get(resolved.keywords)!
  const mapped = resolveMappedMeta({ type, code, confIndex })
  const special = getSpecialMeta({ type, code, confIndex })
  const keyType = mapped?.keyType ?? 'media'

  return {
    keywords: physicalKeywords,
    keyType,
    isModified: true,
    isFactoryDefault: true,
    target: {
      ...factoryMeta,
      ...(special || {}),
      keywords: resolved.keywords,
      keyType,
    } as KeySetting['target'],
    raw: {
      index,
      type,
      code: [code],
      confIndex,
      icon: special?.icon || mapped?.icon || '',
      originalLabel: getLocalizedField(factoryMeta.alias),
    },
  }
}
